//#region imports
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { debounce } from 'lodash';
import moment from 'moment';

// redux
import { useSelector, useDispatch } from 'react-redux';
import { selectUserDb } from '_redux/slices/main-screen/user';
import { setViewTask, setSelectedTaskToView } from '_redux/slices/timeline/selected-task';
import { selectUpdateStatus } from '_redux/slices/timeline/timeline-bookings';
import { selectPaxCheckOutStatus } from '_redux/slices/timeline/timeline-data';

// services
import { searchPassengers } from '_services/passenger-search-service';

// styling
import { Colors, Spacing } from '_styles';

// icons
import { FaWalking } from 'react-icons/fa';
import { IoEyeOutline, IoCloseCircle } from 'react-icons/io5';

// custom components
import JobCard from '_scenes/portal/timeline-v2/job-components/job-card';
import { CustomSpinner } from '_atoms';
import { selectUsedTheme } from '_redux/slices/timeline/theme';

// virtualized list
import { Virtuoso } from 'react-virtuoso';

//#endregion

const PassengerSearch = (props) => {

    // redux
    const dispatch = useDispatch();
    const userDb = useSelector(selectUserDb);
    const iata = userDb?.details?.authority || '';
    const usedTheme = useSelector(selectUsedTheme);
    const bookingUpdateStatus = useSelector(selectUpdateStatus);
    const paxCheckOutStatus = useSelector(selectPaxCheckOutStatus);

    // search states
    const [searchTerm, setSearchTerm] = useState('');
    
    // API results state
    const [searchResults, setSearchResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);

    // Ref to track if component is mounted
    const isMounted = useRef(true);
    // Refs to track previous update statuses
    const prevUpdateStatus = useRef(bookingUpdateStatus);
    const prevCheckOutStatus = useRef(paxCheckOutStatus);

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    // Debounced search function
    const debouncedSearch = useCallback(
        debounce(async (term) => {
            // Reset if search term is too short
            if (!term || term.length < 2) {
                if (isMounted.current) {
                    setSearchResults([]);
                    setHasSearched(false);
                    setError(null);
                }
                return;
            }

            if (isMounted.current) {
                setIsLoading(true);
                setError(null);
                setHasSearched(true);
            }

            try {
                const results = await searchPassengers({
                    iata: iata,
                    date: moment().format('YYYY-MM-DD'),
                    searchTerm: term
                });

                if (isMounted.current) {
                    // Sort results by flight time
                    const sortedResults = (results || []).sort((a, b) => {
                        const getTime = (item) => {
                            const fc = item?.flightConfirmationDetails || item?.flightDetails;
                            if (fc?.departureTime) return new Date(fc.departureTime);
                            if (item?.flightInformation?.scheduledTime) return new Date(item.flightInformation.scheduledTime);
                            if (item?.startTime) return new Date(item.startTime);
                            if (item?.assignedTime) return new Date(item.assignedTime);
                            return new Date();
                        };
                        return getTime(a) - getTime(b);
                    });
                    setSearchResults(sortedResults);
                }
            } catch (err) {
                console.error('Passenger search error:', err);
                if (isMounted.current) {
                    setError(err.message || 'Error searching passengers');
                    setSearchResults([]);
                }
            } finally {
                if (isMounted.current) {
                    setIsLoading(false);
                }
            }
        }, 400),
        [iata]
    );

    // Trigger search when searchTerm changes
    useEffect(() => {
        debouncedSearch(searchTerm);
        
        // Cleanup: cancel pending debounced calls
        return () => {
            debouncedSearch.cancel();
        };
    }, [searchTerm, debouncedSearch]);

    // Re-fetch search results when a booking is updated or checked out
    useEffect(() => {
        const shouldRefresh = hasSearched && searchTerm.length >= 2;
        
        // Check if booking update succeeded
        const bookingUpdated = prevUpdateStatus.current !== 'succeeded' && bookingUpdateStatus === 'succeeded';
        // Check if checkout succeeded
        const checkoutCompleted = prevCheckOutStatus.current !== 'succeeded' && paxCheckOutStatus === 'succeeded';
        
        if (shouldRefresh && (bookingUpdated || checkoutCompleted)) {
            // Re-run the search to get fresh data
            debouncedSearch(searchTerm);
        }
        
        prevUpdateStatus.current = bookingUpdateStatus;
        prevCheckOutStatus.current = paxCheckOutStatus;
    }, [bookingUpdateStatus, paxCheckOutStatus, hasSearched, searchTerm, debouncedSearch]);

    // booking click handler
    const handleBookingClick = useCallback((booking) => {
        if (props.onPassengerSelect) {
            props.onPassengerSelect(booking);
        }
    }, [props.onPassengerSelect]);

    // view task handler
    const handleViewTask = useCallback((booking) => {
        dispatch(setSelectedTaskToView(booking));
        dispatch(setViewTask(true));
    }, [dispatch]);

    // Group results into rows of 4
    const ITEMS_PER_ROW = 4;
    const groupedResults = [];
    for (let i = 0; i < searchResults.length; i += ITEMS_PER_ROW) {
        groupedResults.push(searchResults.slice(i, i + ITEMS_PER_ROW));
    }

    // Memoize item content renderer for Virtuoso - renders a row of 4 items
    const itemContent = useCallback((rowIndex) => {
        const rowItems = groupedResults[rowIndex];
        const isLastRow = rowIndex === groupedResults.length - 1;
        
        return (
            <div
                style={{
                    display: 'flex',
                    gap: Spacing.SCALE_8,
                    paddingBottom: Spacing.SCALE_8,
                    marginBottom: Spacing.SCALE_8,
                    borderBottom: isLastRow ? 'none' : '1px solid #ccc',
                }}
            >
                {rowItems.map((booking, colIndex) => (
                    <div
                        key={booking._id || booking.id || `booking-${rowIndex}-${colIndex}`}
                        style={{
                            flex: 1,
                            minWidth: 0,
                            maxWidth: `calc(25% - ${Spacing.SCALE_6}px)`,
                        }}
                    >
                        <div
                            onClick={() => handleBookingClick(booking)}
                            style={{ cursor: 'pointer' }}
                        >
                            <JobCard 
                                job={booking} 
                                usedTheme={usedTheme}
                                disabled={true}
                                selectedTasksList={[]}
                            />
                        </div>
                        <button
                            onClick={() => handleViewTask(booking)}
                            className='mt-2'
                            style={{
                                width: '100%',
                                padding: Spacing.SCALE_6,
                                backgroundColor: Colors.PRM_LIGHT_BLUE,
                                color: 'white',
                                border: 'none',
                                borderRadius: 4,
                                cursor: 'pointer',
                                fontSize: 12,
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: Spacing.SCALE_4,
                            }}
                        >
                            <IoEyeOutline size={14} />
                            View
                        </button>
                    </div>
                ))}
                {/* Fill empty slots to maintain grid alignment */}
                {rowItems.length < ITEMS_PER_ROW && 
                    Array(ITEMS_PER_ROW - rowItems.length).fill(null).map((_, i) => (
                        <div 
                            key={`empty-${rowIndex}-${i}`} 
                            style={{ 
                                flex: 1, 
                                minWidth: 0,
                                maxWidth: `calc(25% - ${Spacing.SCALE_6}px)`,
                            }} 
                        />
                    ))
                }
            </div>
        );
    }, [groupedResults, handleBookingClick, handleViewTask, usedTheme]);

    // Memoize compute item key for better performance
    const computeItemKey = useCallback((index) => {
        return `row-${index}`;
    }, []);

    return (
        <div style={{
            border: '1px solid lightgrey',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'white',
            borderTopWidth: '4px', borderTopColor: Colors.PRM_LIGHT_BLUE
        }}>

            {/* Search Controls */}
            <div style={{
                padding: Spacing.SCALE_12,
                borderBottom: '1px solid #e0e0e0',
                backgroundColor: '#f9f9f9',
            }}>
                {/* Search Input with Icon */}
                <div style={{ display: 'flex', alignItems: 'center', gap: Spacing.SCALE_8 }}>
                    <FaWalking size={24} color={Colors.PRM_LIGHT_BLUE} />
                    <div style={{ flex: 1, position: 'relative' }}>
                        <input
                            type="text"
                            placeholder="Enter passenger name... (min 2 characters)"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: Spacing.SCALE_8,
                                paddingRight: searchTerm ? '32px' : Spacing.SCALE_8,
                                border: '1px solid #ccc',
                                borderRadius: 4,
                                fontSize: 14,
                                boxSizing: 'border-box',
                            }}
                        />
                        {searchTerm && (
                            <IoCloseCircle
                                size={20}
                                color="#999"
                                onClick={() => setSearchTerm('')}
                                style={{
                                    position: 'absolute',
                                    right: '8px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    cursor: 'pointer',
                                }}
                            />
                        )}
                    </div>
                </div>

                {/* Results Count / Status */}
                <div style={{
                    marginTop: Spacing.SCALE_8,
                    fontSize: 12,
                    color: '#666'
                }}>
                    {isLoading ? (
                        'Searching...'
                    ) : hasSearched ? (
                        `${searchResults.length} result${searchResults.length !== 1 ? 's' : ''} found`
                    ) : (
                        'Enter a search term to find passengers'
                    )}
                </div>
            </div>

            {/* Results List */}
            <div style={{
                flex: 1,
                padding: Spacing.SCALE_8,
                position: 'relative',
            }}>
                {/* Loading State */}
                {isLoading && (
                    <CustomSpinner rowCenter={true} />
                )}

                {/* Error State */}
                {error && !isLoading && (
                    <div style={{
                        textAlign: 'center',
                        padding: Spacing.SCALE_24,
                        color: 'red'
                    }}>
                        {error}
                    </div>
                )}

                {/* Empty State - No search yet */}
                {!isLoading && !error && !hasSearched && (
                    <div style={{
                        textAlign: 'center',
                        padding: Spacing.SCALE_24,
                        color: '#666',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <FaWalking size={64} color={Colors.PRM_LIGHT_BLUE} style={{ marginBottom: Spacing.SCALE_16 }} />
                        <p style={{ marginBottom: Spacing.SCALE_8 }}>Passenger Search</p>
                        <p style={{ fontSize: 12 }}>Enter a passenger name to find matching bookings</p>
                    </div>
                )}

                {/* Empty State - No results */}
                {!isLoading && !error && hasSearched && searchResults.length === 0 && (
                    <div style={{
                        textAlign: 'center',
                        padding: Spacing.SCALE_24,
                        color: '#666'
                    }}>
                        No passengers found matching "{searchTerm}"
                    </div>
                )}

                {/* Results List - Grid of 4 per row */}
                {!isLoading && !error && searchResults.length > 0 && (
                    <Virtuoso
                        style={{ height: '100%', width: '100%' }}
                        totalCount={groupedResults.length}
                        overscan={3}
                        itemContent={itemContent}
                        computeItemKey={computeItemKey}
                    />
                )}
            </div>
        </div>
    );
};

export default PassengerSearch;
